// src/features/executive/insight/widgets/DecisionFactorsBar.tsx
import { Box, Chip, Stack, Typography } from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import { useTranslation } from 'react-i18next'
import CategoricalBar from './CategoricalBar'
import { formatVariance } from 'utils/executive.utils'
import { useDecisionFactors } from '../hooks/use-decision-factors'
import type { DecisionFactorKey } from 'api/executive/executive.types'

const I18N: Record<DecisionFactorKey, string> = {
  price: 'EXECUTIVE.INSIGHT.DECISION.PRICE',
  quality: 'EXECUTIVE.INSIGHT.DECISION.QUALITY',
  location: 'EXECUTIVE.INSIGHT.DECISION.LOCATION',
  referral: 'EXECUTIVE.INSIGHT.DECISION.REFERRAL',
  promotion: 'EXECUTIVE.INSIGHT.DECISION.PROMOTION',
  other: 'EXECUTIVE.INSIGHT.DECISION.OTHER',
}

// Threshold higher than Country/Nationality (10%) because the "ปัจจัยตัดสินใจ"
// field on the register form is optional — clinics often capture < 70% of it.
// Only flag when it's a clear majority of patient records.
const UNKNOWN_WARNING_PCT = 30

// Surface a non-'other' segment with the largest absolute variance vs the
// previous window. DecisionFactors needs both directions (brand erosion in
// "quality" is as important as growth) — hence we don't filter on sign.
const MOVER_MIN_VARIANCE = 0.2

const fmt = (n: number) =>
  new Intl.NumberFormat('th-TH', { maximumFractionDigits: 0 }).format(Math.round(n || 0))

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

export default function DecisionFactorsBar() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useDecisionFactors()

  const segments = data?.segments ?? []
  const total = data?.total ?? 0
  const previousTotal = data?.previousTotal ?? null
  const unknownCount = data?.unknownCount ?? 0
  const totalVariance = varianceOf(total, previousTotal)

  // Data-quality nudge — NULL serviceFactorId folded into 'other'.
  const unknownPct = total > 0 ? (unknownCount / total) * 100 : 0
  const showUnknownWarning = unknownPct > UNKNOWN_WARNING_PCT

  // Largest mover among non-'other' segments with previousCount > 0.
  // We consider both directions (positive and negative) since downward
  // trends in factors like "quality" are equally signal-worthy.
  const mover = segments
    .filter((s) => s.key !== 'other' && s.previousCount != null && s.previousCount > 0)
    .map((s) => ({ s, v: varianceOf(s.count, s.previousCount) }))
    .filter((x) => x.v != null && Math.abs(x.v as number) >= MOVER_MIN_VARIANCE)
    .sort((a, b) => Math.abs(b.v as number) - Math.abs(a.v as number))[0]

  const rows =
    segments.map((s) => ({
      key: s.key,
      label: t(I18N[s.key]),
      count: s.count,
      percent: s.percent,
      previousCount: s.previousCount,
    })) ?? []

  const subtitle = total > 0 && (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
      <span>
        {t('EXECUTIVE.INSIGHT.DECISION.SUBTITLE', '{{count}} ราย', { count: fmt(total) } as any)}
      </span>
      {totalVariance != null && (
        <Typography
          component="span"
          variant="caption"
          sx={{ display: 'inline-flex', alignItems: 'center' }}
          color={totalVariance >= 0 ? 'success.main' : 'error.main'}
        >
          {totalVariance >= 0
            ? <TrendingUpIcon sx={{ fontSize: 13, mr: 0.25 }} />
            : <TrendingDownIcon sx={{ fontSize: 13, mr: 0.25 }} />}
          {formatVariance(totalVariance)}
        </Typography>
      )}
    </Box>
  )

  const notice = (showUnknownWarning || mover) ? (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {showUnknownWarning && (
        <Chip
          icon={<WarningAmberOutlinedIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.DECISION.UNSPECIFIED_WARNING',
                'ไม่ระบุปัจจัยตัดสินใจ {{pct}}% — โปรดตรวจสอบการบันทึกข้อมูลในแบบฟอร์มลงทะเบียน',
                { pct: unknownPct.toFixed(0) }
              )}
            </Typography>
          }
          color="warning"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
      {mover && (mover.v as number) >= 0 && (
        <Chip
          icon={<TrendingUpIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.DECISION.MOVER_UP',
                'ปัจจัยมาแรง: {{label}} ▲ {{pct}}',
                {
                  label: t(I18N[mover.s.key]),
                  pct: formatVariance(mover.v as number),
                }
              )}
            </Typography>
          }
          color="info"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
      {mover && (mover.v as number) < 0 && (
        <Chip
          icon={<TrendingDownIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.DECISION.MOVER_DOWN',
                'ปัจจัยลดลง: {{label}} ▼ {{pct}}',
                {
                  label: t(I18N[mover.s.key]),
                  pct: formatVariance(mover.v as number),
                }
              )}
            </Typography>
          }
          color="warning"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
    </Stack>
  ) : null

  return (
    <CategoricalBar
      title={t('EXECUTIVE.INSIGHT.DECISION.TITLE')}
      titleTooltip={t(
        'EXECUTIVE.INSIGHT.DECISION.SOURCE_TOOLTIP',
        'อ้างอิงจาก patients.serviceFactorId ↔ service_factors.factorName — คำตอบในใบลงทะเบียนถูก map เป็น 6 กลุ่มผ่าน keyword (เช่น "ถูก/คุ้ม/ประหยัด" → "ราคา", "หมอเก่ง/สะอาด/บริการดี" → "คุณภาพการรักษา")\nคำตอบที่ไม่ตรง keyword ใดเลยถูกรวมไว้ใน "อื่น ๆ"'
      )}
      subtitle={subtitle || undefined}
      notice={notice}
      emptyMessage={t(
        'EXECUTIVE.INSIGHT.DECISION.EMPTY',
        'ไม่พบข้อมูลปัจจัยตัดสินใจในช่วงเวลาและเงื่อนไขที่เลือก'
      )}
      rows={rows}
      barColor="#9b59b6"
      loading={loading}
      error={error}
      onRetry={refetch}
    />
  )
}
